import React, { useEffect, useRef, useState } from "react"
import { Box } from "@mui/material"
import { PLACEMENT_ROLES } from "../pages/constants"
import { floodFill, measureRegion } from "../utils/imageFunctions"
import { colorByteToRgb } from "../utils/bluetooth"

// Same grid -> pixel transform as ShowClimb's drawClimb
const GRID_SPACING_X = 2 * (106.0 / 4000) * 1020
const GRID_SPACING_Y = 2 * (82.9 / 3000) * 1024
const SCALE_X = GRID_SPACING_X / 8.0
const SCALE_Y = GRID_SPACING_Y / 8
const OFFSET_X = 25
const OFFSET_Y = -90
const BOARD_HEIGHT = 1024

/**
 * Read-only render of a climb on the kilter board image.
 * `climb` needs `{placements, placement_roles}`; null renders a blank board.
 * `palette` optionally maps role names to wire color bytes (see
 * bluetooth.js) so holds render in the colors the wall will show.
 * `holdStyle` is "fill" (tint the hold shape) or "circle" (ring around it).
 */
export default function BoardCanvas({
  climb,
  palette,
  holdStyle = "fill",
  maxHeight = "45vh",
}) {
  const canvasRef = useRef(null)
  const [holdsImage, setHoldsImage] = useState(null)
  const [resizeCount, setResizeCount] = useState(0)
  // Callers rebuild the palette object every render; compare by value so
  // the canvas only redraws when a color actually changes
  const paletteKey = JSON.stringify(palette ?? null)

  useEffect(() => {
    const image = new Image()
    image.src = "/images/kilter-holds.png"
    image.onload = () => setHoldsImage(image)
  }, [])

  useEffect(() => {
    const onResize = () => setResizeCount((count) => count + 1)
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [])

  useEffect(() => {
    if (!holdsImage || !canvasRef.current) {
      return
    }
    const inMemoryCanvas = document.createElement("canvas")
    inMemoryCanvas.width = holdsImage.width
    inMemoryCanvas.height = holdsImage.height
    const inMemoryContext = inMemoryCanvas.getContext("2d")
    inMemoryContext.drawImage(holdsImage, 0, 0)
    const imageData = inMemoryContext.getImageData(
      0,
      0,
      inMemoryCanvas.width,
      inMemoryCanvas.height
    )
    const placementPixel = (placement) => [
      Math.floor(placement.x * SCALE_X + OFFSET_X),
      Math.floor(BOARD_HEIGHT - placement.y * SCALE_Y - OFFSET_Y),
    ]
    const placementRgb = (index) => {
      const role = PLACEMENT_ROLES[climb.placement_roles[index]]
      const colorByte = palette?.[role?.name]
      return colorByte != null
        ? colorByteToRgb(colorByte)
        : role?.color?.slice(0, 3) || [0, 0, 0]
    }

    if (holdStyle === "circle") {
      // Rings over the bare photo: measure each hold's extent in the
      // (unmodified) holds image, then stroke a circle around it
      inMemoryContext.clearRect(
        0,
        0,
        inMemoryCanvas.width,
        inMemoryCanvas.height
      )
      climb?.placements?.forEach((placement, index) => {
        const [x, y] = placementPixel(placement)
        const [r, g, b] = placementRgb(index)
        const region = measureRegion(imageData, x, y)
        const [cx, cy] = region?.center || [x, y]
        const radius = region
          ? Math.min(Math.max(Math.sqrt(region.area / Math.PI) + 8, 14), 55)
          : 18
        inMemoryContext.beginPath()
        inMemoryContext.arc(cx, cy, radius, 0, 2 * Math.PI)
        inMemoryContext.strokeStyle = `rgb(${r}, ${g}, ${b})`
        inMemoryContext.lineWidth = 6
        inMemoryContext.stroke()
      })
    } else {
      const data = imageData.data
      for (let i = 3; i < data.length; i += 4) {
        data[i] = 64
      }
      climb?.placements?.forEach((placement, index) => {
        const [x, y] = placementPixel(placement)
        floodFill(imageData, x, y, [...placementRgb(index), 64])
      })
      inMemoryContext.putImageData(imageData, 0, 0)
    }

    const canvas = canvasRef.current
    const parent = canvas.parentElement
    canvas.width = parent.offsetWidth
    canvas.height = parent.offsetHeight
    const ctx = canvas.getContext("2d")
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(inMemoryCanvas, 0, 0, canvas.width, canvas.height)
  }, [holdsImage, climb, paletteKey, holdStyle, resizeCount])

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        margin: "auto",
        aspectRatio: "1/1",
        maxHeight,
        maxWidth: maxHeight,
      }}
    >
      <img
        src="/images/kilter.jpg"
        alt="kilter board"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
        }}
      />
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "transparent",
        }}
      />
    </Box>
  )
}
